'use client';

import { changePassword } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useState } from 'react';
import { BsKey } from 'react-icons/bs';
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa';

export const changePasswordSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
      password_old: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      password: __eliadmin_zod_1().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
      password_confirmation: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

type ChangePasswordFormProps = {
  onSubmitSuccess?: () => void;
};

export function ChangePasswordForm(props: ChangePasswordFormProps) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof changePasswordSchema>>>({
    resolver: zodResolver(changePasswordSchema(formValidationMessages)),
    defaultValues: {
      password_old: '',
      password: '',
      password_confirmation: '',
    },
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const { toast } = useToast();
  const router = useRouter();
  const authToken = useCustomerStore(s => s.me!.authToken);

  const handleSubmit = form.handleSubmit(async data => {
    /** Check password validity */
    if (data.password !== data.password_confirmation) {
      /** TODO: preklad */
      form.setError('password_confirmation', { type: 'manual', message: t('text468') });
      form.setError('password', { type: 'manual', message: t('text468') });
      return;
    }

    if (data.password_old === data.password) {
      /** TODO: preklad */
      form.setError('password', { type: 'manual', message: t('text1103') });
      return;
    }

    try {
      await changePassword({ ...data, authToken, locale });
      router.push(ROUTES.PROFILE());
      toast({
        title: t('text649'),
        variant: 'success',
      });
      props.onSubmitSuccess?.();
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text650'),
        description: (e as any).message,
        variant: 'destructive',
      });
    }
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [showPasswordOld, setShowPasswordOld] = useState(false);

  const handlePasswordMouseDown = () => {
    setShowPassword(true);
  };

  const handlePasswordMouseUp = () => {
    setShowPassword(false);
  };

  const handlePasswordConfirmationMouseDown = () => {
    setShowPasswordConfirmation(true);
  };

  const handlePasswordConfirmationMouseUp = () => {
    setShowPasswordConfirmation(false);
  };

  const handlePasswordOldMouseDown = () => {
    setShowPasswordOld(true);
  };

  const handlePasswordOldMouseUp = () => {
    setShowPasswordOld(false);
  };

  return (
    <Form {...form}>
      <form
        className={cn(
          'flex w-full flex-col items-start justify-start gap-6 sm:gap-8 md:gap-10 lg:gap-12',
          'ea-change-password-form',
        )}
        onSubmit={handleSubmit}
      >
        <h4 className="col-span-1 text-2xl font-medium tracking-tight sm:col-span-2 sm:text-3xl">
          {t('text115')}
        </h4>
        <div
          className={cn(
            'grid w-full grid-cols-1 gap-6 sm:grid-cols-2',
            'ea-change-password-form__fields',
          )}
        >
          <FormField
            control={form.control}
            name="password_old"
            render={({ field }) => (
              <FormItem required className="ea-change-password-form__field-password-old">
                <FormControl className="ea-change-password-form__control">
                  <div className="relative">
                    <Input
                      placeholder={t('text202')}
                      type={showPasswordOld ? 'text' : 'password'}
                      {...field}
                      className={{ main: cn('', 'ea-change-password-form__input') }}
                      before={<BsKey size={24} className="text-[#636363]" />}
                    />
                    <button
                      type="button"
                      onMouseDown={handlePasswordOldMouseDown}
                      onMouseUp={handlePasswordOldMouseUp}
                      onMouseLeave={handlePasswordOldMouseUp}
                      onTouchStart={handlePasswordOldMouseDown}
                      onTouchEnd={handlePasswordOldMouseUp}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5 focus:outline-none"
                    >
                      {showPasswordConfirmation ? (
                        <FaRegEyeSlash size={20} className="text-primary" />
                      ) : (
                        <FaRegEye size={20} className="text-primary" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage className="ea-change-password-form__message" />
              </FormItem>
            )}
          />
          <div className="col-span-1 hidden h-[50px] w-full sm:block" />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem required className="ea-change-password-form__field-password-new">
                <FormControl className="ea-change-password-form__control">
                  <div className="relative">
                    <Input
                      placeholder={t('text191')}
                      type={showPassword ? 'text' : 'password'}
                      {...field}
                      className={{ main: cn('', 'ea-change-password-form__input') }}
                      before={<BsKey size={24} className="text-[#636363]" />}
                    />
                    <button
                      type="button"
                      onMouseDown={handlePasswordMouseDown}
                      onMouseUp={handlePasswordMouseUp}
                      onMouseLeave={handlePasswordMouseUp}
                      onTouchStart={handlePasswordMouseDown}
                      onTouchEnd={handlePasswordMouseUp}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5 focus:outline-none"
                    >
                      {showPasswordConfirmation ? (
                        <FaRegEyeSlash size={20} className="text-primary" />
                      ) : (
                        <FaRegEye size={20} className="text-primary" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage className="ea-change-password-form__message" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password_confirmation"
            render={({ field }) => (
              <FormItem required className="ea-change-password-form__field-password-new-repeat">
                <FormControl className="ea-change-password-form__control">
                  <div className="relative">
                    <Input
                      placeholder={t('text1108')}
                      type={showPasswordConfirmation ? 'text' : 'password'}
                      {...field}
                      className={{ main: cn('', 'ea-change-password-form__input') }}
                      before={<BsKey size={24} className="text-[#636363]" />}
                    />
                    <button
                      type="button"
                      onMouseDown={handlePasswordConfirmationMouseDown}
                      onMouseUp={handlePasswordConfirmationMouseUp}
                      onMouseLeave={handlePasswordConfirmationMouseUp}
                      onTouchStart={handlePasswordConfirmationMouseDown}
                      onTouchEnd={handlePasswordConfirmationMouseUp}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5 focus:outline-none"
                    >
                      {showPasswordConfirmation ? (
                        <FaRegEyeSlash size={20} className="text-primary" />
                      ) : (
                        <FaRegEye size={20} className="text-primary" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage className="ea-change-password-form__message" />
              </FormItem>
            )}
          />
        </div>
        <div className={cn('flex flex-col items-start', 'ea-change-password-form__footer')}>
          <Button
            type="submit"
            variant={'success'}
            className={cn('w-full md:w-auto', 'ea-change-password-form__submit-button')}
            isLoading={form.formState.isSubmitting}
          >
            {t('text2363')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
